import React, { useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, TouchableOpacity } from 'react-native'
import StyledTextInput from '../../components/StyledTextInput'
import AdaptiveTableCard from '../../components/AdaptiveTableCard'
import { Icon, Button, Text, Spinner, Radio, useTheme } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import { saveSaleReturn, deleteSale } from '../../global/Firebase/firebase'
import { PaymentMethodsSelect } from '../../components/PaymentMethodsSelect'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { constantsAtom, currencyFormatterAtom, productsDictAtom, storesDictAtom, userRoleAtom } from '../../global/AtomsProvider'
import { getDailyStatID } from '../../utils/timezoneFunctions'
import { returnedLineAmount, returnsMoreThanSold, asCumulativeRefundRefusal } from '../../global/Firebase/returnLineAmount'
import { getFiscalCredentials, getFiscalOperator } from '../../utils/fiscalization'
import { forgetResolvedSales, unfiscalizedSaleKey } from '../../utils/unfiscalizedSales'
import { saveFailureText } from '../../global/Firebase/writeOutcome'

export default function ReturnSaleScreen( { navigation, route } ) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const { sale } = route.params
  const productsDict = useAtomValue(productsDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const constants = useAtomValue(constantsAtom)
  /**
   * ATL-1020 — the store whose fiscal operator governs this sale's return or deletion.
   *
   * It is the SALE's store, because a fiscal void has to reach the cash register that issued the
   * receipt. The delete button read `context.user.store?.id` instead — the cashier's CURRENT store
   * — so an admin deleting another branch's sale voided it at the wrong OFD, and a user with no
   * store at all indexed `storesDict` with `undefined` and crashed. The fallback is the one
   * `returnInfo.store` below already uses.
   *
   * `storeId` is read alongside `store` because that is how every other sale reader in the app
   * spells it (SaleDetailScreen, SalesHistoryScreen, the export transformer): without it a sale
   * carrying only `storeId` would fall through to the cashier's current store and silently
   * reinstate the very defect above.
   */
  const saleStore = storesDict?.[sale.store || sale.storeId || context.user.store?.id]
  const initialReturnItems = () => {
    var newReturnItems = []
    for (const [index, value] of sale.cartItems.entries()) {
      const newValue = {...value, isReturned: true, returnedQuantity: value.quantity.toString(), returnedAmount: value.finalItemPrice}
      newReturnItems.push(newValue)
    }
    return newReturnItems
  }

  const [returnItems, setReturnItems] = useState(initialReturnItems())
  const [returnMethod, setReturnMethod] = useState(null)
  const [selectedReason, setSelectedReason] = useState(null)
  const [returnComment, setReturnComment] = useState('')
  const [saveError, setSaveError] = useState('')
  const [updateStock, setUpdateStock] = useState(false)
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [type, setType] = useState(0)

  const calculateTotalReturnedAmount = () => {
    var totalReturnedAmount = 0
    for (const [index, value] of returnItems.entries()) {
      if (value.isReturned) {
        totalReturnedAmount = totalReturnedAmount + calculateItemReturnAmount(value)
      }
    }
    return totalReturnedAmount
  }

  const calculateReturnedQuantity = () => {
    var totalReturnedQuantity = 0
    for (const [index, value] of returnItems.entries()) {
      if (value.isReturned) {
        totalReturnedQuantity = totalReturnedQuantity + 1
      }
    }
    return totalReturnedQuantity
  }

  /**
   * ATL-930 — the refund for one line is the proportional share of what was CHARGED, never a
   * recomputation from the list price. The rule, the prod measurements behind it and the quantity
   * refusal below live in `global/Firebase/returnLineAmount.ts`, which is unit-tested; this screen
   * only decides when to apply them.
   */
  const calculateItemReturnAmount = (value) => returnedLineAmount(value)

  /** The first returned line asking for more than was sold, if any. `save()` refuses on it. */
  const overReturnedItem = () => returnItems.find((value) => value.isReturned && returnsMoreThanSold(value))

  const save = async () => {
    // update sale cartItems with isReturned, returnedQuantity, returnedAmount
    // update sale with returnInfo - totalReturnedAmount, date, cashier
    const totalReturnedAmount = calculateTotalReturnedAmount()
    const overReturned = overReturnedItem()
    if (overReturned) {
      setSaveError(t('salesHistory.returnQuantityExceedsSold', {
        name: overReturned.name,
        sold: overReturned.quantity,
      }))
      setIsLoading(false)
      return
    }
    if (totalReturnedAmount > 0 && (type == 0 || (type == 1 && returnMethod != null)) && selectedReason != null) {
      setIsLoading(true)
      setSaveError('')
      var updatedCartItems = []
      for (const [index, value] of returnItems.entries()) {
        if (value.isReturned) {
          const newValue = {...value, 
            returnedAmount: calculateItemReturnAmount(value),
            returnedQuantity: parseFloat(value.returnedQuantity),
          }
          updatedCartItems.push(newValue)
        } else {
          updatedCartItems.push(value)
        }
      }
      // ATL-1798: one clock read — the return's date and the daily-stat doc it books into must be
      // the same instant, or a tap at Almaty midnight files the return on one day and its stats on
      // the next.
      const returnDate = new Date()
      var returnInfo = {
        totalReturnedAmount: totalReturnedAmount, 
        date: returnDate, 
        dailyStatID: getDailyStatID(sale.store, returnDate),
        cashier: context.user.uid,
        returnType: type,
        returnMethod: returnMethod,
        updateStock: updateStock,
        returnReason: selectedReason,
        returnComment: returnComment,
        store: sale.store || context.user.store?.id,
        organizationId: context.user.organizationId
      }
      try {
        await saveSaleReturn(db, sale.ref, {
          cartItems: updatedCartItems, 
          returnInfo: returnInfo,
          store: sale.store,
          organizationId: context.user.organizationId
        }, sale.id, sale.cashierUID, getFiscalOperator(saleStore), getFiscalCredentials(saleStore))
        setSuccessMessageVisible(true)
        setIsLoading(false)
      } catch(error) {
        // ATL-959 — the cumulative refund refusal is REPORTED, but not as a crash.
        //
        // It is a business rule doing its job: the cashier asked to refund more than the sale can
        // be refunded. Filing it as an exception would fill QARASAY-KASSA-2A6 with correct
        // behaviour and bury the saves that really do fail. But it is also a refusal of real money
        // at the counter, and the guard's BASIS is the thing that was wrong before (bounding by
        // `finalTotalCartPrice` refused every full return of a loyalty-bonus sale). Suppressing it
        // outright would delete the only telemetry that could show the basis is still wrong for
        // some sale shape we have not measured. So it goes to Sentry as a `warning` with the three
        // numbers attached and its own fingerprint — countable, not noisy, and never mixed in with
        // the crashes.
        //
        // A refusal whose requested amount is at or under the refundable total would be a bug in
        // this rule; the numbers below are exactly what makes that visible.
        const refusal = asCumulativeRefundRefusal(error)
        if (refusal) {
          Sentry.captureMessage('cumulative refund refused at the till', {
            level: 'warning',
            tags: { cumulative_refund_refusal: true, sale_return: 'refused' },
            fingerprint: ['cumulative-refund-refused'],
            contexts: {
              cumulative_refund: {
                saleId: sale.id,
                refundableTotal: refusal.refundableTotal,
                priorReturnedTotal: refusal.priorReturnedTotal,
                requestedRefund: refusal.requestedRefund,
                headroom: refusal.refundableTotal - refusal.priorReturnedTotal,
              },
            },
          })
        } else {
          Sentry.captureException(error, { tags: { books_write_error: 'sale-return-create' } })
        }
        setSaveError(saveFailureText(error, t, error.message))
        setIsLoading(false)
      }
      
    } else {
      setSaveError(t('notAllFieldsFilled'))
      setIsLoading(false)
    }
  }

  const updateReturnItem = (index, changes) => {
    setReturnItems(previousItems => {
      const newReturnData = [...previousItems]
      newReturnData.splice(index, 1, {...previousItems[index], ...changes})
      return newReturnData
    })
  }

  const returnItemFields = [
    {
      key: 'isReturned',
      type: 'checkbox',
      title: t('salesHistory.returnItem'),
      onChange: (index, isChecked) => updateReturnItem(index, {isReturned: isChecked}),
      testID: (item, index) => `return-item-checkbox-${index}`,
    },
    {
      key: 'name',
      type: 'text',
      columnStyle: 'nameColumn',
      title: t('productsAndIngredients.name'),
      textCategory: 's1',
    },
    {
      key: 'returnedQuantity',
      type: 'input',
      title: t('salesHistory.returnQuantity'),
      keyboardType: 'decimal-pad',
      onChange: (index, text) => updateReturnItem(index, {returnedQuantity: text}),
      testID: (item, index) => `return-item-quantity-${index}`,
    },
    {
      key: 'returnedAmount',
      type: 'text',
      title: t('salesHistory.returnItemAmount'),
      formatter: (value, item) => currencyFormatter.format(calculateItemReturnAmount(item)),
    },
  ]

  const renderReturnItemsFooter = () => (
    <Text category='s2' style={styles.totalLabel}>
      {t('salesHistory.totalReturnedAmount', {returnedQuantity: calculateReturnedQuantity(), amount: currencyFormatter.format(calculateTotalReturnedAmount())})}
    </Text>
  )

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        <Text category='label' style={styles.label}>
          {t('salesHistory.chooseReturnItems')}
        </Text>
        <AdaptiveTableCard
          data={returnItems}
          fields={returnItemFields}
          keyExtractor={(item, index) => `${item.id}-${index}`}
          itemTestID={(item, index) => `return-item-${index}`}
          renderFooter={renderReturnItemsFooter}
        />
        
        <Text category='label' style={styles.label}>
          {t('salesHistory.returnType')}
        </Text>
        <Radio style={styles.radio} checked={type==0} onChange={() => {setType(0)}} testID="return-type-replace">
          {t('salesHistory.replaceProduct')}
        </Radio>
        <Radio style={styles.radio} checked={type==1} onChange={(index) => {setType(1)}} testID="return-type-money">
          {t('salesHistory.returnMoney')}
        </Radio>
        { type == 1 && (
          <>
            <Text category='label' style={styles.label}>
              {t('salesHistory.moneyReturnType')}
            </Text>
            <PaymentMethodsSelect paymentMethod={returnMethod} setPaymentMethod={setReturnMethod} paymentMethods={constants?.paymentMethods.data} style={{marginBottom: 15}} store={sale.store || context.user.store.id} />
          </>
        )}
        <Text category='label' style={styles.label}>
          {t('salesHistory.updateStockText')}
        </Text>
        <Radio style={styles.radio} checked={updateStock} onChange={() => {setUpdateStock(true)}} testID="update-stock-yes">
          {t('salesHistory.takeBack')}
        </Radio>
        <Radio style={styles.radio} checked={!updateStock} onChange={() => {setUpdateStock(false)}} testID="update-stock-no">
          {t('salesHistory.writeOff')}
        </Radio>
        <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
          <Text category='label'>
            {t('salesHistory.returnReason')}
          </Text>
          { (userRole?.permissions?.['editReturnCategories'] || context.user.role === 'admin') && (
            <TouchableOpacity onPress={() => navigation.navigate('EditableList', {atom: constantsAtom, initialDataPath: ['returnReasons', 'data'], title: t('salesHistory.returnReasons'), ref: constants?.returnReasons.ref})} style={{marginLeft: 5, padding: 3}}>
              <Icon name='settings-outline' height={15} width={15} fill={theme['color-primary-500']} />
            </TouchableOpacity>
          )}
        </View>
        <PlatformDependantSelect
          data={constants?.returnReasons.data}
          selectedOption={selectedReason}
          onSelect={(itemValue, itemIndex) => {
            setSelectedReason(itemValue)
          }}
          testID="return-reason-select"
        />
        <Text category='label' style={styles.label}>
          {t('salesHistory.comment')}
        </Text>
        <StyledTextInput
          autoCapitalize='sentences'
          value={returnComment}
          onChangeText={text => {
            setReturnComment(text)
          }}
          testID="return-comment-input"
        />
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.navigate('Home')} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && 
          <>
            <Button style={styles.submitButton} appearance='outline' status='primary' onPress={save} testID="submit-return-button">
              {t('salesHistory.return')}
            </Button>
            { (userRole?.permissions?.deleteSale || context.user.role === 'admin') && (
              <Button style={styles.deleteButton} appearance='outline' status='danger' testID="delete-sale-button" onPress={async () => {
                try {
                  setIsLoading(true)
                  await deleteSale(db, sale.ref, sale, context.user.uid, getFiscalOperator(saleStore), getFiscalCredentials(saleStore))
                  // ATL-1274 review — a deleted sale is no longer an UNFISCALIZED sale. New Sale's
                  // till-local register (utils/unfiscalizedSales.ts) is otherwise pruned only when
                  // a retry returns a `fiscalInfo`, so without this the red banner would go on
                  // counting a sale that no longer exists, and its call to action would point at a
                  // Sales History with nothing left to retry. The register is a count the cashier
                  // reads; a count that is wrong is not a lesser problem for being local.
                  const deletedSaleId = unfiscalizedSaleKey(sale)
                  if (deletedSaleId) {
                    setContext(ctx => ({...ctx, unfiscalizedSales: forgetResolvedSales(ctx.unfiscalizedSales, [deletedSaleId])}))
                  }
                  setIsLoading(false)
                  // navigation.navigate('SalesHistory')
                  navigation.navigate('Home')
                } catch(error) {
                  console.log(error)
                  setSaveError(t('error'))
                  Sentry.captureException(error, { tags: { books_write_error: 'sale-delete' } })
                  setIsLoading(false)
                }
              }}>
                {t('salesHistory.deleteSale')}
              </Button>
            )}
          </>
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  label: {
    marginVertical: 10,
  },
  totalLabel: {
    marginVertical: 10,
  },
  submitButton: {
    marginTop: 15,
  },
  deleteButton: {
    marginTop: 15,
    marginBottom: 60,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  radio: {
    marginBottom: 5
  }
});